import { useCallback, useEffect, useRef, useState } from 'react';

import { useT } from './i18n';
import { ROUTES, useRoute } from './lib/router';
import { createInitialState } from './domain/state';
import type {
  ApartmentInfo,
  ConditionInfo,
  OwnerContact,
  SelectedFile,
  SubmissionPayload,
  SubmissionErrorCode,
  WizardState,
} from './domain/types';
import { MAX_PHOTOS } from './domain/types';
import {
  focusFirstError,
  parseDecimal,
  validateStep1,
  validateStep2,
  validateStep3,
  STEP1_FIELD_ORDER,
  STEP2_FIELD_ORDER,
  STEP3_FIELD_ORDER,
  type Errors,
} from './lib/validation';
import { revokePreview } from './lib/imageProcessing';
import { submitSubmission } from './lib/api';

import { TopoBackground } from './components/TopoBackground';
import { Assurance } from './components/Assurance';
import { Stepper } from './components/Stepper';
import { StepApartment } from './components/StepApartment';
import { StepCondition } from './components/StepCondition';
import { StepContact } from './components/StepContact';
import { Acknowledgement } from './components/Acknowledgement';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ScoreGateDialog } from './components/ScoreGateDialog';
import { ConsentBanner } from './components/ConsentBanner';
import { onConsentChange, readConsent, resetConsent } from './lib/consent';
import { META_EVENTS, isMetaConfigured, loadMetaPixel, trackMeta } from './lib/meta';
import { readProvenance } from './lib/provenance';
import './App.css';

const TOTAL_STEPS = 3;

/** Scroll target for the hero's call to action. */
const WIZARD_ANCHOR_ID = 'formular';

export function App() {
  const t = useT();
  const [route, navigate] = useRoute();

  const [state, setState] = useState<WizardState>(createInitialState);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmissionErrorCode | null>(null);
  const [submitted, setSubmitted] = useState<{ id: string; email: string } | null>(null);

  /*
   * The soft score gate: the localised phrases for the score-critical fields
   * still empty on step 1, or null when the dialog is closed. Once the owner
   * explicitly chooses to continue without them, we do not ask again.
   */
  const [scoreGate, setScoreGate] = useState<string[] | null>(null);
  const scoreGateSkipped = useRef(false);

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /*
   * A returning visitor who already agreed on an earlier visit: load without
   * asking again. A fresh decision is the card's job, not this one's.
   */
  useEffect(() => {
    if (readConsent() === 'granted') loadMetaPixel();
  }, []);

  const setApartment = useCallback((update: Partial<ApartmentInfo>) => {
    setState((prev) => ({ ...prev, apartment: { ...prev.apartment, ...update } }));
    // An error must not outlive its fix: clear a gate error the moment the field
    // holds what it asked for, rather than at the next press of Continue.
    setErrors((prev) => {
      let next = prev;
      if (update.address !== undefined && update.address.trim() !== '') next = stripKey(next, 'address');
      if (update.suprafataUtila !== undefined) {
        const value = parseDecimal(update.suprafataUtila);
        if (value !== null && value > 0) next = stripKey(next, 'suprafataUtila');
      }
      return next;
    });
  }, []);

  const setCondition = useCallback((update: Partial<ConditionInfo>) => {
    setState((prev) => ({ ...prev, condition: { ...prev.condition, ...update } }));
  }, []);

  const setContact = useCallback((update: Partial<OwnerContact>) => {
    setState((prev) => ({ ...prev, contact: { ...prev.contact, ...update } }));
  }, []);

  const addPhotos = useCallback((entries: SelectedFile[]) => {
    setState((prev) => ({
      ...prev,
      files: { ...prev.files, photos: [...prev.files.photos, ...entries].slice(0, MAX_PHOTOS) },
    }));
    setErrors((prev) => stripKey(prev, 'photos'));
  }, []);

  const removePhoto = useCallback((id: string) => {
    setState((prev) => {
      const entry = prev.files.photos.find((p) => p.id === id);
      if (entry) revokePreview(entry);
      return { ...prev, files: { ...prev.files, photos: prev.files.photos.filter((p) => p.id !== id) } };
    });
  }, []);

  const setPlan = useCallback((entries: SelectedFile[]) => {
    const next = entries[0];
    if (!next) return;
    setState((prev) => {
      if (prev.files.plan) revokePreview(prev.files.plan);
      return { ...prev, files: { ...prev.files, plan: next } };
    });
    setErrors((prev) => stripKey(prev, 'plan'));
  }, []);

  const removePlan = useCallback(() => {
    setState((prev) => {
      if (prev.files.plan) revokePreview(prev.files.plan);
      return { ...prev, files: { ...prev.files, plan: null } };
    });
  }, []);

  const setPhotoError = useCallback((message: string | null) => {
    setErrors((prev) => (message ? { ...prev, photos: message } : stripKey(prev, 'photos')));
  }, []);
  const setPlanError = useCallback((message: string | null) => {
    setErrors((prev) => (message ? { ...prev, plan: message } : stripKey(prev, 'plan')));
  }, []);

  /*
   * Warn before an accidental close. The owner may have spent ten minutes
   * photographing rooms, and none of it is recoverable — by design, nothing is
   * persisted client-side.
   */
  const hasEnteredData =
    state.apartment.address.trim() !== '' ||
    state.files.photos.length > 0 ||
    state.files.plan !== null ||
    state.contact.email.trim() !== '';

  useEffect(() => {
    if (!hasEnteredData || submitted) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasEnteredData, submitted]);

  const scrollToWizard = useCallback(() => {
    const target = document.getElementById(WIZARD_ANCHOR_ID);
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, []);

  const goToStep = useCallback(
    (next: number) => {
      setStep(next);
      setErrors({});
      scrollToWizard();
    },
    [scrollToWizard],
  );

  const startFromHero = useCallback(() => {
    trackMeta(META_EVENTS.startedForm);
    scrollToWizard();
  }, [scrollToWizard]);

  const advanceFromStep1 = () => {
    const found = validateStep1(state.apartment, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, STEP1_FIELD_ORDER);
      return;
    }
    /*
     * The soft score gate: year + seismic class unlock the score, but they must
     * never block the lead. Empty (not "nu știu" — that is an answer) opens the
     * dialog once; an explicit "continue without" is remembered and respected.
     */
    const missing: string[] = [];
    if (state.apartment.anConstructie === '') missing.push(t.step1.scoreGate.missingAn);
    if (state.apartment.incadrareSeismica === '') missing.push(t.step1.scoreGate.missingSeismic);
    if (missing.length > 0 && !scoreGateSkipped.current) {
      setScoreGate(missing);
      return;
    }
    /*
     * The campaign's optimisation target. Fires here, reached only once the
     * apartment has an address and a surface and the owner chose to carry on —
     * a materially better predictor than an abandoned first field.
     */
    trackMeta(META_EVENTS.apartmentLocated, true);
    goToStep(2);
  };

  /** "Completez datele": close the gate and land on the first empty score field. */
  const scoreGateComplete = useCallback(() => {
    setScoreGate(null);
    const target = state.apartment.anConstructie === '' ? 'anConstructie' : 'incadrareSeismica';
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-field="${target}"]`);
      if (!el) return;
      el.focus({ preventScroll: true });
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    }, 0);
  }, [state.apartment.anConstructie]);

  /** "Continui fără scor": remembered, so the dialog never nags twice. */
  const scoreGateSkip = useCallback(() => {
    scoreGateSkipped.current = true;
    setScoreGate(null);
    trackMeta(META_EVENTS.apartmentLocated, true);
    goToStep(2);
  }, [goToStep]);

  const advanceFromStep2 = () => {
    const found = validateStep2(state.files, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, STEP2_FIELD_ORDER);
      return;
    }
    goToStep(3);
  };

  const goAddPhotos = () => {
    setStep(2);
    // Land on the photo control the notice named, not the top of the step.
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>('[data-field="photos"]');
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  const submit = async () => {
    const found = validateStep3(state.contact, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, STEP3_FIELD_ORDER);
      return;
    }

    // Re-check the earlier steps: the owner could have edited them and walked
    // forward again, and the server will reject anything malformed anyway.
    const earlier = { ...validateStep1(state.apartment, t), ...validateStep2(state.files, t) };
    if (Object.keys(earlier).length > 0) {
      setErrors(earlier);
      const step1Keys = STEP1_FIELD_ORDER as readonly string[];
      const onStep1 = Object.keys(earlier).some((k) => step1Keys.includes(k));
      const target = onStep1 ? 1 : 2;
      setStep(target);
      window.setTimeout(
        () => focusFirstError(earlier, onStep1 ? STEP1_FIELD_ORDER : STEP2_FIELD_ORDER),
        0,
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const payload = buildPayload(state);

    try {
      const result = await submitSubmission(payload, state.files, controller.signal);
      if (result.ok) {
        trackMeta(META_EVENTS.lead);
        // The wizard is leaving the screen — release the preview URLs.
        state.files.photos.forEach(revokePreview);
        if (state.files.plan) revokePreview(state.files.plan);
        setSubmitted({ id: result.id, email: state.contact.email.trim() });
        return;
      }

      setSubmitError(result.code);
      if (result.fields) {
        setErrors(result.fields);
        const step2Keys = STEP2_FIELD_ORDER as readonly string[];
        const step1Keys = STEP1_FIELD_ORDER as readonly string[];
        if (Object.keys(result.fields).some((k) => step1Keys.includes(k))) setStep(1);
        else if (Object.keys(result.fields).some((k) => step2Keys.includes(k))) setStep(2);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setSubmitError('server');
    } finally {
      setSubmitting(false);
    }
  };

  if (route === ROUTES.privacy) {
    return (
      <Shell>
        <PrivacyPolicy onBack={() => navigate(ROUTES.wizard)} />
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <Acknowledgement />
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="wizard__header">
        <h1>{t.app.title}</h1>
        <p className="lede">{t.app.intro}</p>

        <button type="button" className="btn btn--hero" onClick={startFromHero}>
          {t.app.ctaLabel}
          <span className="btn__arrow" aria-hidden="true">
            →
          </span>
        </button>

        <p className="wizard__cta-note">{t.app.ctaNote}</p>
      </header>

      <div className="wizard__anchor" id={WIZARD_ANCHOR_ID}>
        <Stepper
          current={step}
          total={TOTAL_STEPS}
          labels={[t.steps.apartment, t.steps.condition, t.steps.contact]}
          onNavigate={goToStep}
        />

        <div className="wizard__step">
          {step === 1 && (
            <StepApartment apartment={state.apartment} errors={errors} onChange={setApartment} />
          )}

          {step === 2 && (
            <StepCondition
              condition={state.condition}
              files={state.files}
              errors={errors}
              onConditionChange={setCondition}
              onAddPhotos={addPhotos}
              onRemovePhoto={removePhoto}
              onSetPlan={setPlan}
              onRemovePlan={removePlan}
              onPhotoError={setPhotoError}
              onPlanError={setPlanError}
            />
          )}

          {step === 3 && (
            <StepContact
              contact={state.contact}
              apartment={state.apartment}
              files={state.files}
              errors={errors}
              onContactChange={setContact}
              onGoAddPhotos={goAddPhotos}
            />
          )}
        </div>
      </div>

      {submitError && (
        <div className="submit-error" role="alert">
          <h3>{t.submit.errorHeading}</h3>
          <p>{errorMessage(submitError, t)}</p>
        </div>
      )}

      <div className="wizard__nav">
        {step > 1 && (
          <button type="button" className="btn" onClick={() => goToStep(step - 1)} disabled={submitting}>
            {t.nav.back}
          </button>
        )}
        <div className="wizard__nav-spacer" />

        {step === 1 && (
          <button type="button" className="btn" onClick={advanceFromStep1}>
            {t.nav.continue}
          </button>
        )}
        {step === 2 && (
          <button type="button" className="btn" onClick={advanceFromStep2}>
            {t.nav.continue}
          </button>
        )}
        {step === 3 && (
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting && <span className="spinner" aria-hidden="true" />}
            {submitError ? t.submit.retry : t.nav.submit}
          </button>
        )}
      </div>

      <Assurance />

      {scoreGate && (
        <ScoreGateDialog missing={scoreGate} onComplete={scoreGateComplete} onSkip={scoreGateSkip} />
      )}
    </Shell>
  );
}

function stripKey(errors: Errors, key: string): Errors {
  if (!errors[key]) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}

function errorMessage(code: SubmissionErrorCode, t: ReturnType<typeof useT>): string {
  switch (code) {
    case 'network':
      return t.submit.errorNetwork;
    case 'validation':
      return t.submit.errorValidation;
    case 'rate_limit':
      return t.submit.errorRateLimit;
    case 'too_large':
      return t.submit.errorTooLarge;
    default:
      return t.submit.errorGeneric;
  }
}

function buildPayload(state: WizardState): SubmissionPayload {
  const { apartment, condition, contact } = state;
  return {
    locale: 'ro',
    address: apartment.address.trim(),
    lat: apartment.lat,
    lng: apartment.lng,
    anConstructie: apartment.anConstructie === '' ? null : apartment.anConstructie,
    tipImobil: apartment.tipImobil === '' ? null : apartment.tipImobil,
    etaj: apartment.etaj.trim() === '' ? null : apartment.etaj.trim(),
    etajeTotal: apartment.etajeTotal.trim() === '' ? null : apartment.etajeTotal.trim(),
    suprafataUtila: parseDecimal(apartment.suprafataUtila),
    numarCamere: apartment.numarCamere === '' ? null : apartment.numarCamere,
    incadrareSeismica: apartment.incadrareSeismica === '' ? null : apartment.incadrareSeismica,
    lift: apartment.lift,
    compartimentare: apartment.compartimentare === '' ? null : apartment.compartimentare,
    balcon: apartment.balcon === '' ? null : apartment.balcon,
    suprafataBalcon: parseDecimal(apartment.suprafataBalcon),
    sursaIncalzire: apartment.sursaIncalzire === '' ? null : apartment.sursaIncalzire,
    suprafataTeren: parseDecimal(apartment.suprafataTeren),
    structura: apartment.structura === '' ? null : apartment.structura,
    incalzire: apartment.incalzire === '' ? null : apartment.incalzire,
    stareActuala: condition.stareActuala === '' ? null : condition.stareActuala,
    situatieJuridica: condition.situatieJuridica === '' ? null : condition.situatieJuridica,
    intentieNote: condition.intentieNote.trim(),
    ownerName: contact.name.trim(),
    ownerEmail: contact.email.trim(),
    ownerPhone: contact.phone.trim(),
    consentGiven: contact.consent,
    consentTimestamp: new Date().toISOString(),
    referrer: readProvenance(),
  };
}

function Shell({ children }: { children: React.ReactNode }) {
  const t = useT();
  const [, navigate] = useRoute();

  return (
    <>
      <TopoBackground />

      <a className="skip-link" href="#main">
        {t.a11y.skipToContent}
      </a>

      <header className="masthead">
        <div className="masthead__inner">
          <a
            className="masthead__brand"
            href={ROUTES.wizard}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(ROUTES.wizard);
            }}
          >
            <span className="masthead__wordmark" translate="no">
              {t.brand.wordmark}
            </span>
          </a>
          <span className="masthead__tagline">{t.brand.tagline}</span>
        </div>
      </header>

      <main id="main" className="shell">
        {children}
      </main>

      <footer className="colophon">
        <div className="colophon__inner">
          <p>{t.footer.rights(new Date().getFullYear())}</p>
          <a href={ROUTES.privacy} target="_blank" rel="noopener noreferrer">
            {t.footer.privacy}
          </a>
          <ConsentReset />
        </div>
      </footer>

      <ConsentBanner />
    </>
  );
}

/**
 * "Setări de măsurare" — reopens the consent card by clearing the stored answer.
 * Renders only once a decision exists and the pixel is configured.
 */
function ConsentReset() {
  const t = useT();
  const [state, setState] = useState(() => readConsent());

  useEffect(() => onConsentChange(setState), []);

  if (!isMetaConfigured() || state === 'unset') return null;

  return (
    <button type="button" className="btn-link cookie-notice-reset" onClick={resetConsent}>
      {t.footer.consentReset}
    </button>
  );
}
